import { useCallback, useEffect, useRef, useState } from 'react';
import type { BoardSummary, Item } from '@scribbleplot/shared';
import { slugify } from '@scribbleplot/shared';
import { api } from '../lib/api';
import { downloadJson, readTextFile } from '../lib/download';
import { useBoardPreview } from '../lib/previews';
import { initialsOf, useSession } from '../lib/session';
import type { Navigate } from '../lib/router';
import { Avatar, Toast } from '../ui/primitives';
import { ConfirmDialog } from '../ui/overlays';
import { ActionMenu } from '../ui/ActionMenu';
import { useNotice } from '../ui/notifications';
import { color } from '../tokens';

export function HomeScreen({ accent, navigate }: { accent: string; navigate: Navigate }) {
  const user = useSession((s) => s.user);
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [deleting, setDeleting] = useState<BoardSummary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { notice, notify } = useNotice();
  const load = useCallback(async () => {
    setLoadError(false);
    setBoards(null);
    try {
      const { boards } = await api.listBoards();
      setBoards(boards);
    } catch {
      setLoadError(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const action = async (work: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      await work();
    } catch (e) {
      notify({ kind: 'error', message: e instanceof Error ? e.message : 'Please try again.' });
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const create = () =>
    void action(async () => {
      const { board } = await api.createBoard('Untitled board');
      navigate({ name: 'board', id: board.id });
    });
  const duplicate = (board: BoardSummary) =>
    void action(async () => {
      const { board: copy } = await api.duplicateBoard(board.id);
      setBoards((current) => [copy, ...(current ?? [])]);
      notify({ kind: 'success', message: `Copied to “${copy.name}”` });
    });
  const exportBoard = (board: BoardSummary) =>
    void action(async () => {
      const file = await api.exportBoard(board.id);
      downloadJson(`${slugify(board.name)}.scribbleplot.json`, file);
      notify({ kind: 'success', message: `Exported “${board.name}” as an editable Scribbleplot board file` });
    });
  const importBoard = (file: File) =>
    void action(async () => {
      const payload = JSON.parse(await readTextFile(file));
      const { board, images } = await api.importBoard(payload);
      setBoards((current) => [board, ...(current ?? [])]);
      notify({
        kind: images.skipped ? 'error' : 'success',
        message: images.skipped
          ? `Imported “${board.name}”; ${images.skipped} images could not be read.`
          : `Imported “${board.name}”`,
      });
    });
  return (
    <div className="sp-screen" style={{ '--sp-accent': accent } as React.CSSProperties}>
      <header className="sp-page-header">
        <span className="sp-brand-mark" aria-hidden>
          ↗
        </span>
        <span className="sp-wordmark">Scribbleplot</span>
        <span style={{ flex: 1 }} />
        <Avatar
          initials={initialsOf(user?.name)}
          accent={accent}
          onClick={() => navigate({ name: 'settings' })}
          title="Settings"
        />
      </header>
      <main className="sp-page-body">
        <div className="sp-gallery">
          <div className="sp-gallery-heading">
            <div>
              <h1>Your boards</h1>
              <p>A little space for your next big idea.</p>
            </div>
            <div className="sp-gallery-actions">
              <button className="sp-button" disabled={busy} onClick={() => fileRef.current?.click()}>
                Import board
              </button>
              <button className="sp-button sp-primary" disabled={busy} aria-busy={busy} onClick={create}>
                <span aria-hidden>+</span> New board
              </button>
            </div>
          </div>
          {loadError ? (
            <div className="sp-empty" role="alert">
              <h2>We couldn’t load your boards</h2>
              <p>Your work is still there. Check your connection and try again.</p>
              <button className="sp-button" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : boards === null ? (
            <div className="sp-board-grid" role="status" aria-label="Loading boards">
              {[0, 1, 2].map((n) => (
                <div className="sp-skeleton" key={n} />
              ))}
            </div>
          ) : boards.length ? (
            <div className="sp-board-grid">
              {boards.map((board) => (
                <BoardCard
                  key={board.id}
                  board={board}
                  userId={user?.id ?? ''}
                  accent={accent}
                  busy={busy}
                  onOpen={() => navigate({ name: 'board', id: board.id })}
                  onDuplicate={() => duplicate(board)}
                  onExport={() => exportBoard(board)}
                  onDelete={() => setDeleting(board)}
                />
              ))}
            </div>
          ) : (
            <div className="sp-empty">
              <h2>Make room for an idea</h2>
              <p>Start with a note, an image, or a thought you don’t want to lose.</p>
              <button className="sp-button sp-primary" disabled={busy} onClick={create}>
                Create your first board
              </button>
            </div>
          )}
        </div>
      </main>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        aria-label="Import a board file"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) importBoard(file);
        }}
      />
      {deleting && (
        <ConfirmDialog
          title={`Delete “${deleting.name}”?`}
          description="This permanently deletes the board for everyone who has access. This cannot be undone. Export a board file first if you want to keep a copy."
          confirmLabel="Delete board"
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await api.deleteBoard(deleting.id);
            setBoards((current) => current?.filter((b) => b.id !== deleting.id) ?? []);
            notify('Board deleted');
          }}
        />
      )}
      <Toast message={notice} />
    </div>
  );
}
function BoardCard({
  board,
  userId,
  accent,
  busy,
  onOpen,
  onDuplicate,
  onExport,
  onDelete,
}: {
  board: BoardSummary;
  userId: string;
  accent: string;
  busy: boolean;
  onOpen: () => void;
  onDuplicate: () => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  const { ref, items, failed } = useBoardPreview(board, userId);
  return (
    <div ref={ref} className="sp-board-card">
      <button
        aria-label={`Open ${board.name}`}
        onClick={onOpen}
        style={{
          display: 'block',
          width: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
          color: color.text,
        }}
      >
        {failed ? (
          <div style={{ height: 168, display: 'grid', placeItems: 'center', color: color.textMuted, fontSize: 12 }}>
            Preview unavailable
          </div>
        ) : (
          <Thumbnail items={items} accent={accent} />
        )}
        <div style={{ padding: '18px 20px' }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 650,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {board.name}
          </div>
          <div style={{ fontSize: 12, color: color.textMuted, marginTop: 7 }}>
            {board.itemCount} {board.itemCount === 1 ? 'item' : 'items'} ·{' '}
            {board.role === 'owner'
              ? board.isPublic
                ? 'Public link'
                : 'Private'
              : board.role === 'editor'
                ? 'Can edit'
                : 'View only'}
          </div>
        </div>
      </button>
      <ActionMenu
        label={`Actions for ${board.name}`}
        actions={[
          { label: `Export ${board.name}`, run: onExport, disabled: busy },
          { label: `Duplicate ${board.name}`, run: onDuplicate, disabled: busy },
          ...(board.role === 'owner'
            ? [{ label: `Delete ${board.name}`, run: onDelete, danger: true, disabled: busy }]
            : []),
        ]}
      />
    </div>
  );
}

/** Projects a board's items into the card's aspect box. */
function Thumbnail({ items, accent }: { items: Item[] | undefined; accent: string }) {
  const frame = {
    height: 168,
    position: 'relative' as const,
    overflow: 'hidden',
    background: color.canvas,
    backgroundImage: 'radial-gradient(rgba(255,255,255,.09) 1px,transparent 1px)',
    backgroundSize: '18px 18px',
    borderBottom: `1px solid ${color.line}`,
  };

  if (!items?.length) {
    return (
      <div style={frame}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color.textGhost,
            fontSize: 12,
            fontFamily: 'inherit',
          }}
        >
          {items === undefined ? 'Loading preview…' : 'A fresh canvas'}
        </div>
      </div>
    );
  }

  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const item of items) {
    x0 = Math.min(x0, item.x);
    y0 = Math.min(y0, item.y);
    x1 = Math.max(x1, item.x + item.w);
    y1 = Math.max(y1, item.y + item.h);
  }
  const width = Math.max(1, x1 - x0);
  const height = Math.max(1, y1 - y0);

  return (
    <div style={frame}>
      {items.map((item) => {
        const fill =
          item.type === 'sticky'
            ? item.color
            : item.type === 'group'
              ? accent
              : item.type === 'frame'
                ? 'transparent'
                : '#3E4250';
        return (
          <div
            key={item.id}
            style={{
              position: 'absolute',
              // 88% × 80% of the card, inset, so nothing touches the edges.
              left: `${6 + ((item.x - x0) / width) * 88}%`,
              top: `${8 + ((item.y - y0) / height) * 80}%`,
              width: `${Math.max(1.5, (item.w / width) * 88)}%`,
              height: `${Math.max(2, (item.h / height) * 80)}%`,
              background: fill,
              ...(item.type === 'image' ? { backgroundImage: `url("${item.src}")`, backgroundSize: 'cover' } : {}),
              border: item.type === 'frame' ? `1px solid rgba(255,255,255,.22)` : 'none',
              borderRadius: 2,
              opacity: 0.9,
            }}
          />
        );
      })}
    </div>
  );
}
